import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Sparkles, Loader2, Layers, FileDown, Save, Box, Share2, Wand2, Check, RefreshCw } from "lucide-react";
import {
  COUNTRIES,
  BUILDING_TYPES,
  FINISH_LEVELS,
  US_REGIONS,
  CA_REGIONS,
} from "../mock";
import { getAIEstimate, saveProject, generateRender, renderImageUrl, createShare, getSwaps } from "../lib/api";
import { exportEstimatePDF } from "../lib/pdf";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const Calculator = () => {
  const { user, login } = useAuth();
  const { toast } = useToast();
  const [country, setCountry] = useState("US");
  const [type, setType] = useState("single_family");
  const [size, setSize] = useState(2000);
  const [finish, setFinish] = useState("standard");
  const [region, setRegion] = useState("national");
  const [description, setDescription] = useState("");
  const [targetBudget, setTargetBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastRequest, setLastRequest] = useState(null);
  const [renderId, setRenderId] = useState(null);
  const [renderLoading, setRenderLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [swaps, setSwaps] = useState(null);
  const [swapsLoading, setSwapsLoading] = useState(false);
  const [appliedSwap, setAppliedSwap] = useState(null);
  const [prevTotal, setPrevTotal] = useState(null);

  const regions = country === "US" ? US_REGIONS : CA_REGIONS;
  const countryObj = COUNTRIES.find((c) => c.code === country);

  const fmt = (n) =>
    `${countryObj.symbol}${Math.round(n).toLocaleString("en-US")}`;

  const estimate = () => {
    setLoading(true);
    setResult(null);
    setAiResult(null);
    setAiError(null);
    setTimeout(() => {
      const bt = BUILDING_TYPES.find((b) => b.id === type);
      const fl = FINISH_LEVELS.find((f) => f.id === finish);
      const rg = regions.find((r) => r.id === region) || regions[0];
      const fx = country === "CA" ? 1.36 : 1;
      const rate = bt.baseRate * fl.mult * rg.mult * fx;
      const total = rate * Number(size || 0);
      const material = total * 0.54;
      const labor = total * 0.46;
      setResult({
        total,
        low: total * 0.9,
        high: total * 1.12,
        rate,
        material,
        labor,
        typeName: bt.name,
      });
      setLoading(false);
    }, 900);
  };

  const getBreakdown = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    setRenderId(null);
    setSwaps(null);
    setAppliedSwap(null);
    setPrevTotal(null);
    try {
      const bt = BUILDING_TYPES.find((b) => b.id === type);
      const fl = FINISH_LEVELS.find((f) => f.id === finish);
      const rg = regions.find((r) => r.id === region) || regions[0];
      const payload = {
        country: countryObj.name,
        building_type: bt.name,
        size_sqft: Number(size || 0),
        finish_level: fl.name,
        region: rg.name,
        description: description || undefined,
        target_budget: targetBudget ? Number(targetBudget) : undefined,
      };
      const data = await getAIEstimate(payload);
      setAiResult(data.result);
      setLastRequest(payload);
    } catch (e) {
      setAiError("Couldn't generate the AI breakdown. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const symbolFor = (cur) => (cur === "CAD" ? "CA$" : "$");

  const buildRenderPrompt = () => {
    const bt = BUILDING_TYPES.find((b) => b.id === type);
    const fl = FINISH_LEVELS.find((f) => f.id === finish);
    return description?.trim()
      ? description
      : `${fl.name.toLowerCase()} finish ${bt.name.toLowerCase()}, ${size} sq ft, North American style`;
  };

  const handleRender = async () => {
    if (!user) {
      toast({ title: "Sign in to render", description: "Free accounts get 3 render points. Redirecting…" });
      setTimeout(login, 800);
      return;
    }
    setRenderLoading(true);
    try {
      const data = await generateRender({ prompt: buildRenderPrompt(), angle: "exterior" });
      setRenderId(data.id);
      toast({ title: "3D view ready", description: "It will be attached when you save this project." });
    } catch (e) {
      if (e?.response?.status === 402) {
        toast({ title: "Out of render points", description: "Upgrade or buy points in the Pricing section." });
      } else {
        toast({ title: "Render failed", description: "Please try again." });
      }
    } finally {
      setRenderLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast({ title: "Sign in to save", description: "Redirecting you to Google sign-in…" });
      setTimeout(login, 800);
      return;
    }
    setSaving(true);
    try {
      const bt = BUILDING_TYPES.find((b) => b.id === type);
      await saveProject({
        name: `${bt.name} · ${size} sq ft`,
        request: lastRequest,
        result: aiResult,
        render_id: renderId || undefined,
      });
      toast({ title: "Project saved!", description: "Find it in your dashboard." });
    } catch (e) {
      toast({ title: "Couldn't save", description: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const bt = BUILDING_TYPES.find((b) => b.id === type);
      const { slug } = await createShare({
        name: `${bt.name} · ${size} sq ft`,
        request: lastRequest,
        result: aiResult,
        render_id: renderId || undefined,
      });
      const url = `${window.location.origin}/share/${slug}`;
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: "Share link copied!", description: url });
      } catch (e) {
        toast({ title: "Share link ready", description: url });
      }
    } catch (e) {
      toast({ title: "Couldn't create link", description: "Please try again." });
    } finally {
      setSharing(false);
    }
  };

  const loadSwaps = async () => {
    setSwapsLoading(true);
    try {
      const data = await getSwaps({ request: lastRequest, result: aiResult });
      setSwaps(data.swaps || []);
    } catch (e) {
      toast({ title: "Couldn't load swaps", description: "Please try again." });
    } finally {
      setSwapsLoading(false);
    }
  };

  const applySwap = async (swap) => {
    setAiLoading(true);
    setAppliedSwap(swap.title);
    setPrevTotal({ low: aiResult.total_low, high: aiResult.total_high });
    try {
      const newDesc = `${description ? description + ". " : ""}Apply this cost-saving change: ${swap.title} — ${swap.detail}`;
      const payload = { ...lastRequest, description: newDesc };
      const data = await getAIEstimate(payload);
      setAiResult(data.result);
      setLastRequest(payload);
      setRenderId(null);
      toast({ title: `Applied: ${swap.title}`, description: "Estimate re-run with your swap." });
    } catch (e) {
      toast({ title: "Couldn't re-run", description: "Please try again." });
      setAppliedSwap(null);
      setPrevTotal(null);
    } finally {
      setAiLoading(false);
    }
  };

  const goStudio = () => {
    const el = document.querySelector("#studio");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handlePDF = () => {
    const bt = BUILDING_TYPES.find((b) => b.id === type);
    exportEstimatePDF({ request: lastRequest, result: aiResult, name: `${bt.name} · ${size} sq ft` });
  };

  return (
    <section id="estimator" className="py-24 bg-slate-50 bp-grid">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-semibold uppercase tracking-wider text-[#DC2626]">
            Try it now
          </span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-700 tracking-tight text-[#0A2540]">
            What will your build cost?
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Get an instant national-average ballpark in seconds — no signup required.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-7">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-slate-700">Country</Label>
                <Select value={country} onValueChange={(v) => { setCountry(v); setRegion("national"); }}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Building type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUILDING_TYPES.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Size (sq ft)</Label>
                <Input
                  type="number"
                  value={size}
                  min={100}
                  onChange={(e) => setSize(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-700">Finish level</Label>
                <Select value={finish} onValueChange={setFinish}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FINISH_LEVELS.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5">
              <Label className="text-slate-700">Describe your project (optional · any language)</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="e.g. 3-bed home with open kitchen, 2-car garage and a covered deck… / décrivez votre projet…"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/40"
              />
              <p className="mt-1.5 text-xs text-slate-400">Write in any language — the AI reads it and tailors the scope breakdown.</p>
            </div>

            <div className="mt-4">
              <Label className="text-slate-700">Target budget (optional)</Label>
              <div className="mt-2 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{countryObj.symbol}</span>
                <Input
                  type="number"
                  value={targetBudget}
                  onChange={(e) => setTargetBudget(e.target.value)}
                  placeholder="e.g. 350000"
                  className="pl-11"
                />
              </div>
              <p className="mt-1.5 text-xs text-slate-400">Set a budget and the AI will value-engineer the build to fit it.</p>
            </div>

            <Button
              onClick={estimate}
              disabled={loading}
              className="mt-5 w-full h-12 text-base bg-[#DC2626] hover:bg-[#b91c1c] text-white"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Estimating…</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> Estimate cost</>
              )}
            </Button>
          </div>

          {/* Result */}
          <div className="rounded-2xl bg-[#0A2540] text-white shadow-lg p-7 flex flex-col">
            {!result && !loading && (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
                <div className="h-14 w-14 rounded-full bg-white/10 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-[#DC2626]" />
                </div>
                <p className="mt-4 text-slate-300 max-w-xs">
                  Pick your country and build, then hit <span className="font-semibold text-white">Estimate cost</span> for an instant local-currency number.
                </p>
              </div>
            )}
            {loading && (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" />
              </div>
            )}
            {result && !loading && (
              <div className="animate-fade-up">
                <div className="text-sm text-slate-300">{result.typeName} · estimated total</div>
                <div className="font-display text-5xl font-700 mt-1">{fmt(result.total)}</div>
                <div className="text-sm text-slate-400 mt-2">
                  Range {fmt(result.low)} – {fmt(result.high)} · {fmt(result.rate)}/sq ft
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300">Materials</span>
                      <span className="font-semibold">{fmt(result.material)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/10">
                      <div className="h-2 rounded-full bg-[#1D4ED8]" style={{ width: "54%" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300">Labor</span>
                      <span className="font-semibold">{fmt(result.labor)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/10">
                      <div className="h-2 rounded-full bg-[#DC2626]" style={{ width: "46%" }} />
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-lg bg-white/5 border border-white/10 p-4">
                  <button
                    onClick={getBreakdown}
                    disabled={aiLoading}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#1D4ED8] hover:bg-[#1e40af] text-white text-sm font-semibold py-2.5 transition-colors disabled:opacity-60"
                  >
                    {aiLoading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Generating AI breakdown…</>
                    ) : (
                      <><Layers className="h-4 w-4" /> Get AI scope-by-scope breakdown</>
                    )}
                  </button>
                  {aiError && (
                    <p className="mt-3 text-sm text-red-300">{aiError}</p>
                  )}
                </div>

                {aiResult && (
                  <div className="mt-5 animate-fade-up">
                    {targetBudget && (
                      <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-semibold px-3 py-1">
                        <Check className="h-3.5 w-3.5" /> Value-engineered to fit {countryObj.symbol}{Number(targetBudget).toLocaleString("en-US")} target
                      </div>
                    )}
                    {aiResult.summary && (
                      <p className="text-sm text-slate-300 leading-relaxed">{aiResult.summary}</p>
                    )}
                    <div className="mt-4 rounded-lg border border-white/10 overflow-hidden">
                      <div className="grid grid-cols-12 bg-white/5 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-400">
                        <span className="col-span-6">Scope</span>
                        <span className="col-span-3 text-right">Material</span>
                        <span className="col-span-3 text-right">Labor</span>
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-white/5">
                        {(aiResult.scopes || []).map((s, i) => {
                          const sym = symbolFor(aiResult.currency);
                          return (
                            <div key={i} className="grid grid-cols-12 px-3 py-2.5 text-sm items-center">
                              <span className="col-span-6 text-slate-200">{s.name}</span>
                              <span className="col-span-3 text-right text-slate-300">
                                {sym}{Math.round(s.material || 0).toLocaleString("en-US")}
                              </span>
                              <span className="col-span-3 text-right text-slate-300">
                                {sym}{Math.round(s.labor || 0).toLocaleString("en-US")}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="mt-3 flex justify-between text-sm">
                      <span className="text-slate-400">AI total range</span>
                      <span className="font-semibold text-white">
                        {symbolFor(aiResult.currency)}{Math.round(aiResult.total_low || 0).toLocaleString("en-US")} – {symbolFor(aiResult.currency)}{Math.round(aiResult.total_high || 0).toLocaleString("en-US")}
                      </span>
                    </div>
                    {appliedSwap && prevTotal && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-emerald-300">
                        <Check className="h-3.5 w-3.5" /> Applied “{appliedSwap}” · was {symbolFor(aiResult.currency)}{Math.round(prevTotal.low).toLocaleString("en-US")}–{symbolFor(aiResult.currency)}{Math.round(prevTotal.high).toLocaleString("en-US")}
                      </div>
                    )}
                    <p className="mt-2 text-[11px] text-slate-500">
                      AI-generated ballpark · not a substitute for a licensed contractor quote.
                    </p>

                    {/* Render preview */}
                    {renderId && (
                      <div className="mt-4 rounded-lg overflow-hidden border border-white/10">
                        <img src={renderImageUrl(renderId)} alt="3D view" className="w-full h-40 object-cover" />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-5 grid grid-cols-2 gap-2">
                      <button
                        onClick={handleRender}
                        disabled={renderLoading}
                        className="flex items-center justify-center gap-1.5 rounded-lg bg-[#1D4ED8] hover:bg-[#1e40af] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60 col-span-2"
                      >
                        {renderLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Rendering 3D view…</> : <><Box className="h-4 w-4" /> {renderId ? "Regenerate 3D view" : "Generate 3D view for this build"}</>}
                      </button>
                      <button onClick={handleSave} disabled={saving} className="flex items-center justify-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                      </button>
                      <button onClick={handleShare} disabled={sharing} className="flex items-center justify-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60">
                        {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share
                      </button>
                      <button onClick={handlePDF} className="flex items-center justify-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 transition-colors">
                        <FileDown className="h-4 w-4" /> PDF
                      </button>
                      <button onClick={goStudio} className="flex items-center justify-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 transition-colors">
                        <Box className="h-4 w-4" /> Studio
                      </button>
                    </div>

                    {/* Material swaps */}
                    <div className="mt-4 rounded-lg bg-white/5 border border-white/10 p-4">
                      {!swaps ? (
                        <button
                          onClick={loadSwaps}
                          disabled={swapsLoading}
                          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white py-1.5 disabled:opacity-60"
                        >
                          {swapsLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Finding savings…</> : <><Wand2 className="h-4 w-4 text-[#f87171]" /> Suggest cost-saving swaps</>}
                        </button>
                      ) : (
                        <div className="space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs uppercase tracking-wider text-slate-400">AI value-engineering</span>
                            <button onClick={loadSwaps} className="text-slate-400 hover:text-white" title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
                          </div>
                          {swaps.map((sw, i) => (
                            <div key={i} className="rounded-lg bg-white/5 p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-sm font-semibold text-white">{sw.title} <span className="text-[#f87171] font-medium">· save {sw.savings}</span></div>
                                  <div className="text-xs text-slate-400 mt-0.5">{sw.detail}</div>
                                </div>
                                <button
                                  onClick={() => applySwap(sw)}
                                  disabled={aiLoading}
                                  className="shrink-0 rounded-md bg-[#DC2626] hover:bg-[#b91c1c] text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-60"
                                >
                                  Apply &amp; re-run
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
